/* eslint-disable react-refresh/only-export-components */
import { ReactNode, createContext, useCallback, useContext, useEffect, useState } from 'react'

import {
  BrandResponse,
  ChannelResponse,
  CompanyClipperResponse,
  CompanyResponse,
  Paged,
  PromptRulesResponse,
  PublisherPersonResponse,
  PublisherResponse,
  SpokespersonResponse,
  ThemeGroupResponse,
  ThemeResponse,
} from '@shared/models'

import { Api } from '@/modules'

import { Routes } from '@/configs'
import { useRouting } from '@/hooks'
import { PublisherFilters } from './publishers/Publishers.table'

export type ChannelFilters = {
  name?: string
  page?: number | string
  size?: number
  onlyActive?: boolean
}

export type ThemeFilters = {
  brands?: string
  themeGroups?: string
  isActive?: boolean
}

interface CompanyDetailsContextOutput {
  isLoadingCompany: boolean
  company?: CompanyResponse
  reloadCompany: () => void

  isLoadingBrands: boolean
  brands?: Paged<BrandResponse>
  reloadBrands: (page: string) => void
  isLoadingAllBrands: boolean
  allBrands: BrandResponse[]
  reloadAllBrands: () => Promise<void>

  isLoadingThemeGroups: boolean
  themeGroups?: Paged<ThemeGroupResponse>
  reloadThemeGroups: (page: string, isActive?: boolean) => void

  isLoadingThemes: boolean
  themes?: Paged<ThemeResponse>
  themeFilters: ThemeFilters
  setThemeFilters: (filters: ThemeFilters) => void
  reloadThemes: (params: NodeJS.Dict<any>) => void

  isLoadingChannels: boolean
  channels?: Paged<ChannelResponse>
  loadChannels: (filters: ChannelFilters) => void

  isLoadingSpokespeople: boolean
  spokespeople?: Paged<SpokespersonResponse>
  reloadSpokesPeople: (page: string, onlyActive?: boolean, name?: string) => void

  isLoadingPublishers: boolean
  publishers?: Paged<PublisherResponse>
  reloadPublishers: (page: PublisherFilters) => void

  isLoadingCompanyClippers: boolean
  companyClippers?: Paged<CompanyClipperResponse>
  reloadCompanyClippers: (page: string) => void

  isFetchingCompanyCompetitors: boolean
  companyCompetitors?: Paged<CompanyResponse>
  fetchCompanyCompetitors: (params: NodeJS.Dict<any>) => void

  isLoadingPromptRules: boolean
  promptRules?: PromptRulesResponse[]
  reloadPromptRules: () => void

  isLoadingPublisherPersons: boolean
  publisherPersons?: Paged<PublisherPersonResponse>
  reloadPublisherPersons: (params: NodeJS.Dict<any>) => void
}

interface ContextProps {
  children: ReactNode
}

const CompanyDetailsContext = createContext<CompanyDetailsContextOutput>({} as CompanyDetailsContextOutput)

export function CompanyDetailsContextProvider({ children }: ContextProps) {
  const { companyID, navigate } = useRouting()

  const [themeFilters, setThemeFilters] = useState<ThemeFilters>({
    brands: undefined,
    themeGroups: undefined,
    isActive: true,
  })

  const [isLoadingCompany, reloadCompany, company, findCompanyError] = Api.Query<CompanyResponse>(
    `companies/${companyID}`,
  )
  const [loadBrands, isLoadingBrands, brands] = Api.LazyQuery<Paged<BrandResponse>>(`brands?company=${companyID}`)
  const [fetchBrandsPage, isLoadingAllBrands] = Api.PromiseQuery<Paged<BrandResponse>>(`brands?company=${companyID}`)
  const [loadThemeGroups, isLoadingThemeGroups, themeGroups] = Api.LazyQuery<Paged<ThemeGroupResponse>>(
    `theme-groups?company=${companyID}`,
  )
  const [loadThemes, isLoadingThemes, themes] = Api.LazyQuery<Paged<ThemeResponse>>(`themes?company=${companyID}`)
  const [loadChannels, isLoadingChannels, channels] = Api.LazyQuery<Paged<ChannelResponse>>(
    `channels?company=${companyID}`,
  )
  const [loadSpokespersons, isLoadingSpokespeople, spokespersons] = Api.LazyQuery<Paged<SpokespersonResponse>>(
    `spokespersons?company=${companyID}`,
  )
  const [loadPublishers, isLoadingPublishers, publishers] = Api.LazyQuery<Paged<PublisherResponse>>(
    `companies/${companyID}/publishers`,
  )
  const [loadCompanyClippers, isLoadingCompanyClippers, companyClippers] = Api.LazyQuery<Paged<CompanyClipperResponse>>(
    `companies/${companyID}/clippers`,
  )
  const [fetchCompanyCompetitors, isFetchingCompanyCompetitors, companyCompetitors] = Api.LazyQuery<
    Paged<CompanyResponse>
  >(`companies?motherID=${companyID}`)
  const [loadPromptRules, isLoadingPromptRules, promptRules] = Api.LazyQuery<PromptRulesResponse[]>(
    `prompt-rules?companyID=${companyID}`,
  )

  const [loadPublisherPersons, isLoadingPublisherPersons, publisherPersons] = Api.LazyQuery<
    Paged<PublisherPersonResponse>
  >(`companies/${companyID}/publisher-persons`)
  const [allBrands, setAllBrands] = useState<BrandResponse[]>([])

  const size = 10

  useEffect(() => {
    if (findCompanyError) return navigate(Routes.Home.path)
  }, [findCompanyError])

  const handleReloadBrands = useCallback(
    (page: string) => {
      loadBrands({ size, page })
    },
    [loadBrands],
  )

  const handleReloadAllBrands = useCallback(async (): Promise<void> => {
    if (!companyID) {
      setAllBrands([])
      return
    }

    const loadedBrands: BrandResponse[] = []
    let currentPage = 1
    let totalPages = 1

    while (currentPage <= totalPages) {
      const [response, error] = await fetchBrandsPage(undefined, { size: 200, page: currentPage })

      if (error || !response) {
        break
      }

      loadedBrands.push(...(response.elements || []))
      totalPages = response.pages || 1
      currentPage += 1
    }

    const uniqueBrands = Array.from(new Map(loadedBrands.map((brand) => [brand.id, brand])).values())
    setAllBrands(uniqueBrands)
  }, [companyID, fetchBrandsPage])

  const handleReloadThemeGroups = useCallback(
    (page: string, isActive: boolean = true) => {
      loadThemeGroups({ size, page, isActive: String(isActive) })
    },
    [loadThemeGroups],
  )

  const handleReloadThemes = useCallback(
    (params: NodeJS.Dict<any>): void => {
      loadThemes({ ...params, size })
    },
    [loadThemes],
  )

  const handleLoadChannels = useCallback(
    (filters: ChannelFilters): void => {
      if (!filters) {
        filters = {}
      }

      if (!filters.page) {
        filters.page = 1
      }

      if (!filters.size) {
        filters.size = size
      }

      if (filters.onlyActive === undefined) {
        filters.onlyActive = true
      }

      loadChannels(filters)
    },
    [loadChannels],
  )

  const handleReloadSpokesPeople = useCallback(
    (page: string, onlyActive: boolean = true, name?: string) => {
      loadSpokespersons({
        size,
        page,
        onlyActive: String(onlyActive),
        ...(name ? { name } : {}),
      })
    },
    [loadSpokespersons],
  )

  const handleReloadPublishers = useCallback(
    (filters: PublisherFilters): void => {
      if (!filters) {
        filters = {}
      }

      if (!filters.page) {
        filters.page = 1
      }

      if (!filters.size) {
        filters.size = size
      }

      loadPublishers(filters)
    },
    [loadPublishers],
  )

  const handleReloadCompanyClippers = useCallback(
    (page: string): void => {
      loadCompanyClippers({ size, page })
    },
    [loadCompanyClippers],
  )

  const handleFetchCompanyCompetitors = useCallback(
    (params: NodeJS.Dict<any>): void => {
      fetchCompanyCompetitors({ size, ...params })
    },
    [fetchCompanyCompetitors],
  )

  const handleReloadPromptRules = useCallback(() => {
    loadPromptRules()
  }, [loadPromptRules])

  const handleReloadPublisherPersons = useCallback(
    (params: NodeJS.Dict<any>): void => {
      loadPublisherPersons({ ...params })
    },
    [loadPublisherPersons],
  )

  return (
    <CompanyDetailsContext.Provider
      value={{
        isLoadingCompany,
        company,
        reloadCompany,

        isLoadingBrands,
        brands,
        reloadBrands: handleReloadBrands,
        isLoadingAllBrands,
        allBrands,
        reloadAllBrands: handleReloadAllBrands,

        isLoadingThemeGroups,
        themeGroups,
        reloadThemeGroups: handleReloadThemeGroups,

        isLoadingThemes,
        themes,
        themeFilters,
        setThemeFilters,
        reloadThemes: handleReloadThemes,

        isLoadingChannels,
        channels,
        loadChannels: handleLoadChannels,

        isLoadingSpokespeople,
        spokespeople: spokespersons,
        reloadSpokesPeople: handleReloadSpokesPeople,

        isLoadingPublishers,
        publishers,
        reloadPublishers: handleReloadPublishers,

        isLoadingCompanyClippers,
        companyClippers,
        reloadCompanyClippers: handleReloadCompanyClippers,

        isFetchingCompanyCompetitors,
        companyCompetitors,
        fetchCompanyCompetitors: handleFetchCompanyCompetitors,

        isLoadingPromptRules,
        promptRules,
        reloadPromptRules: handleReloadPromptRules,

        isLoadingPublisherPersons,
        publisherPersons,
        reloadPublisherPersons: handleReloadPublisherPersons,
      }}>
      {children}
    </CompanyDetailsContext.Provider>
  )
}

export function useCompanyContext(): CompanyDetailsContextOutput {
  const context = useContext(CompanyDetailsContext)
  if (!context) {
    throw new Error('useCompanyContext deve ser chamado dentro do CompanyDetailsContextProvider!')
  }
  return context
}
